import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, FormActions, PageHeader } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FaixaEtapasIncompletas, {
  useEtapasIncompletas,
} from '../../../components/adesao/EtapasIncompletas';
import CapturaDocumentos, {
  HandleCapturaDocumentos,
} from '../../../components/documento/CapturaDocumentos';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';

import { RootState, store } from '../../../store/store';
import { addCarroData, setAssociadoId } from '../../../store/associadoSlice';

import { PRODUTO } from '../../produto/routes';

import { createAssociado, fetchAssociadoPorId } from '../../../api/associado/associadoApi';
import { parseMoeda } from '../../../utils/moeda';
import { rgParaEnvio } from '../../../utils/mascaras';
import { isCarroVivo } from '../../../utils/carroSituacao';
import { salvarEtapaRascunho } from '../../../utils/adesaoFluxo';

/**
 * `/documento` — **etapa 3 da adesão** (reescrita na at-60, frente C/C3).
 *
 * 🔴 **É a tela de maior risco do fluxo, e não tem um único campo de formulário**
 * (achado C9 do `plan-c`): é aqui que o `POST /associado/cadastrar` é montado,
 * lendo `state.associado` e `state.codigo` **por nome de chave** — ~30 campos,
 * com o endereço duplicado dentro de `carro`, o tratamento da motocicleta
 * elétrica, o `parseMoeda` e a leitura do bloco `envioSga` (at-36).
 *
 * ⚠️ **A montagem do payload abaixo NÃO foi tocada.** Ela é o contrato entre os
 * slices e o backend, e os rascunhos já gravados no banco guardam exatamente
 * esse formato (at-38). O que esta branch mudou é envelope, feedback e navegação.
 *
 * ## O que mudou de comportamento
 *
 *   1. **Fim dos 3 segundos** de espera antes de navegar para `/produto`. O toast
 *      sobrevive à navegação (container único no `index.tsx`, at-19).
 *   2. **Câmera e prévia saíram para um componente compartilhado** com
 *      `/documento/edit` ({@link CapturaDocumentos}) — eram ~120 linhas idênticas
 *      nas duas telas.
 *   3. **Sem `alert()` nativo** na falha de câmera, e **sem o "✖"** no botão de
 *      remover foto (emoji na interface, proibido desde 06/09/2026).
 *   4. **Stepper** dizendo "Etapa 3 de 6".
 *
 * ⚠️ O `FullScreenLoader` **fica** aqui, ao contrário das etapas 1 e 2: existe
 * espera real e longa a cobrir (cadastro no backend + envio imediato ao SGA +
 * upload das fotos), e nada na tela pode ser tocado nesse intervalo.
 */
const Documento: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const captura = useRef<HandleCapturaDocumentos>(null);
  const [gravando, setGravando] = useState(false);

  /*
   * at-62: etapa anterior incompleta trava a captura e o cadastro. Ver
   * `components/adesao/EtapasIncompletas` — o que ela acusa é subconjunto do que
   * a DTO exige, então bloquear não fecha porta que estivesse aberta.
   */
  const { bloqueado } = useEtapasIncompletas(3);

  const volunteerCode = useSelector((state: RootState) => state.auth.codigoAssociado);
  const associado = useSelector((state: any) => state.associado);
  const codigo = useSelector((state: any) => state.codigo);

  // at-30: parse centralizado por estrutura — o antigo /\D/g + /100 virava 1/100
  // do valor quando a string não estava mascarada com 2 casas
  const parseCurrency = (value: string) => parseMoeda(value);
  const numeroOuIndefinido = (n: number) => (Number.isFinite(n) ? n : undefined);

  const concluirCadastro = async () => {
    setGravando(true);
    try {
      const associadoData = {
        nomeCompleto: associado.nomeCompleto,
        cpfCnpj: associado.cpfCnpj.replace(/\D/g, ''),
        // at-58 (D5): tira a pontuação e PRESERVA as letras. Era
        // `replace(/\D/g,'')`, que com o RG alfanumérico apagaria a letra do
        // órgão emissor em silêncio — ver `rgParaEnvio`.
        rg: rgParaEnvio(associado.rg),
        dataNascimento: associado.dataNascimento,
        sexo: associado.sexo.toUpperCase(),
        email: associado.email,
        dddCelular: associado.dddCelular,
        celular: associado.celular,
        /*
         * at-58 (D2, decisão de Victor de 09/09/2026): o telefone fixo passa a
         * ser ENVIADO. Era campo morto — coletado na etapa 1, guardado no estado
         * e descartado aqui, embora as colunas existam e sejam opcionais.
         *
         * ⚠️ **Os nomes divergem, e isto é a ponte:** no estado do front a chave é
         * `dddTelefone`; a coluna e a DTO são `dddTelefoneFixo`. Mandar
         * `dddTelefone` derrubaria o cadastro INTEIRO com 400 — o `ValidationPipe`
         * global usa `forbidNonWhitelisted`, e campo não declarado na DTO recusa
         * a requisição.
         *
         * ⚠️ `undefined` quando vazio (e não `''`): a coluna é opcional, e string
         * vazia gravaria "telefone em branco" em vez de "não informado".
         */
        dddTelefoneFixo: associado.dddTelefone || undefined,
        telefoneFixo: associado.telefoneFixo || undefined,
        regional: associado.regionalId,
        rua: associado.rua,
        numero: associado.numero,
        complemento: associado.complemento,
        bairro: associado.bairro,
        cidade: associado.cidade,
        estado: associado.estado,
        cep: associado.cep,
        volunteerCode: volunteerCode,
        carro: {
          // Motocicleta elétrica (at-46): o id do catálogo é o que faz o backend
          // gerar a identificação, aceitar os campos vazios e ligar o modo bike.
          ...(associado.carro.bikeEletricaId
            ? { bikeEletricaId: associado.carro.bikeEletricaId }
            : {}),
          // at-81: 0km vai sem placa e sem renavam — o backend gera a identificação
          // provisória pelo chassi. Só mandar quando verdadeiro: a DTO o declara.
          ...(associado.carro.zeroKm && !associado.carro.bikeEletricaId ? { zeroKm: true } : {}),
          placa: associado.carro.placa,
          anoModelo: associado.carro.anoModelo,
          anoFabricacao: associado.carro.anoFabricacao,
          modelo: associado.carro.modelo,
          chassi: associado.carro.chassi,
          renavam: associado.carro.renavam,
          cooperativa: codigo.codigoCooperativa,
          cor: associado.carro.corId,
          combustivel: associado.carro.combustivelId,
          cambio: associado.carro.cambioId,
          tipoVeiculo: codigo.codigoTipoVeiculo,
          categoria: associado.carro.categoriaId,
          codigoFipe: associado.carro.codigoFipe,
          // NaN (campo vazio — caso da bike, que não tem FIPE) vira undefined, não
          // null: `undefined` é omitido do JSON e o DTO trata como "não informado".
          valorFipe: numeroOuIndefinido(parseCurrency(associado.carro.valorFipe)),
          valorProtegido: parseCurrency(associado.carro.valorProtegido),
          cota: associado.carro.cotaId,
          vencimentoDesejado: associado.carro.vencimentoDesejado,
          valorCota: parseFloat(associado.carro.valorCota),
          endereco: {
            rua: associado.rua,
            numero: associado.numero,
            complemento: associado.complemento,
            bairro: associado.bairro,
            cidade: associado.cidade,
            estado: associado.estado,
            cep: associado.cep,
          },
        },
      };

      const responseCreateAssociado = await createAssociado(associadoData);
      const associadoId = responseCreateAssociado.id;
      dispatch(setAssociadoId(associadoId));

      // Rascunho (at-38): a etapa vira PRODUTO com o `associadoId` — o backend
      // resolve o `carroId` a partir dele. Sem `await` e sem bloquear o fluxo.
      void salvarEtapaRascunho(dispatch, () => store.getState(), 'PRODUTO', {
        associadoId,
      });

      // O `associado.create` do backend não faz `include` dos carros, então o
      // id do veículo vem desta leitura — é o que dá dono aos documentos.
      let carroId: string | undefined;
      try {
        const associadoSalvo = await fetchAssociadoPorId(associadoId);
        const carroSalvo = (associadoSalvo?.carros ?? []).filter(isCarroVivo)[0];
        carroId = carroSalvo?.id;
        // at-81: o 0km foi enviado SEM placa e o backend gerou a identificação
        // provisória (`0KM-<chassi>`). As etapas seguintes acham o veículo pela placa
        // do fluxo — sem isto, `/termo` cairia no primeiro carro do associado.
        if (associado.carro.zeroKm && carroSalvo?.placa) {
          dispatch(addCarroData({ placa: carroSalvo.placa }));
        }
      } catch {
        // segue sem o carroId: documento com dono parcial é melhor que órfão
      }

      await captura.current?.enviarDocumentos(associadoId, carroId);

      // at-36 (item 25): o backend tenta enviar ao SGA na hora. O cadastro local já deu
      // certo aqui — o `envioSga` só diz se o envio pegou ou se ficou para o cron.
      const envioSga = responseCreateAssociado?.envioSga;
      const tudoEnviado =
        envioSga?.associado === 'ENVIADO' &&
        (envioSga?.veiculos ?? []).every((v: any) => v.status === 'ENVIADO');

      if (envioSga && tudoEnviado) {
        toast.success('Associado e veículo cadastrados e enviados ao SGA', {
          autoClose: 5000,
          closeOnClick: true,
          theme: 'colored',
        });
      } else if (envioSga) {
        toast.success('Associado e veículo cadastrados com sucesso', {
          autoClose: 4000,
          closeOnClick: true,
          theme: 'colored',
        });
        // Pendência não é erro: o cadastro está salvo e o envio automático (2h) cobre.
        toast.info(
          envioSga.detalhe ?? 'O envio ao SGA será feito automaticamente em instantes.',
          { autoClose: 7000, closeOnClick: true },
        );
      } else {
        toast.success('Associado e veículo cadastrados com sucesso', {
          autoClose: 5000,
          closeOnClick: true,
          theme: 'colored',
        });
      }

      // ⚠️ Sem `setTimeout`. O toast sobrevive à navegação (container único).
      navigate(PRODUTO());
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(axiosError.response?.data?.message || 'Erro ao finalizar o cadastro.');
      setGravando(false);
    }
  };

  return (
    <ShellSaac
      cabecalho={<PageHeader titulo="Documentos" sobretitulo="Nova Adesão" aoVoltar={voltar} />}
    >
      {gravando && <FullScreenLoader mensagem="Cadastrando associado e veículo..." />}

      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={3} fluxo={"ADESAO"} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              {/*
                at-62: etapa anterior incompleta **bloqueia** esta — o cadastro
                seria recusado (os campos que faltam são `@IsNotEmpty` na DTO) e
                as fotos se perderiam, porque o upload só acontece depois dele.
              */}
              <FaixaEtapasIncompletas etapa={3} fluxo="ADESAO" />

              <CapturaDocumentos
                ref={captura}
                prefixoFalhaUpload="O cadastro foi feito"
                desabilitado={gravando || bloqueado}
              />

              <FormActions
                apoio={
                  bloqueado
                    ? 'Complete as etapas anteriores para cadastrar.'
                    : 'As fotos são opcionais e podem ser reenviadas depois.'
                }
              >
                <Botao
                  variante="primario"
                  larguraTotal
                  carregando={gravando}
                  disabled={bloqueado}
                  onClick={() => void concluirCadastro()}
                >
                  {gravando ? 'Cadastrando...' : 'Cadastrar Associado e Veículo'}
                </Botao>
              </FormActions>
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default Documento;
